import { useState, useCallback, useEffect } from 'react';
import {
  StepId,
  STEP_DEFINITIONS,
  getNextStep,
  getTrainingPurposeOptions,
  StepOption,
} from './onboardingQuestions';
import {
  saveOnboardingAnswers,
  loadOnboardingAnswers,
} from '../../services/onboardingStorage';
import {
  OnboardingData,
  RaceEvent,
  UserDistance,
  TrainingDistance,
  CommunityParticipation,
  TrainingIntensity,
} from '../../types/user';
import {
  DistanceUnit,
  ExperienceLevel,
  RunningCommunity,
} from '../../types/enums';
import { OnboardingSessionData } from '../../types/onboardingSession';

const FIRST_STEP: StepId = 'initial_onboarding';

export interface UseOnboardingFlow {
  currentStepId: StepId;
  stepDefinition: typeof STEP_DEFINITIONS[StepId];
  answers: Record<string, any>;
  stepHistory: StepId[];
  progress: number;
  currentAnswer: any;

  /** Store answer without advancing (for text inputs, pickers, multi-select) */
  setAnswer: (value: any) => void;
  /** Store answer AND advance to next step (for single-select auto-advance) */
  answerAndAdvance: (value: any) => void;
  /** Advance to next step using current answers */
  advance: () => void;
  goBack: () => void;
  canGoBack: boolean;
  canSkip: boolean;
  isLastStep: boolean;
  isAnswered: boolean;

  getOptions: () => StepOption[];
  getOnboardingSessionData: () => OnboardingSessionData;
}

export function useOnboardingFlow(): UseOnboardingFlow {
  const [answers, setAnswers] = useState<Record<string, any>>(() => {
    return loadOnboardingAnswers() || {};
  });
  const [stepHistory, setStepHistory] = useState<StepId[]>(() => {
    const saved = loadOnboardingAnswers();
    if (saved) {
      // Rebuild history from saved answers
      const history: StepId[] = [FIRST_STEP];
      let current: StepId = FIRST_STEP;
      while (true) {
        const next = getNextStep(current, saved);
        if (!next || !saved.hasOwnProperty(next)) break;
        history.push(next);
        current = next;
      }
      // Add the next unanswered step
      const nextUnanswered = getNextStep(current, saved);
      if (nextUnanswered && !saved.hasOwnProperty(nextUnanswered)) {
        history.push(nextUnanswered);
      }
      return history.length > 0 ? history : [FIRST_STEP];
    }
    return [FIRST_STEP];
  });

  const currentStepId = stepHistory[stepHistory.length - 1];
  const stepDefinition = STEP_DEFINITIONS[currentStepId];
  const currentAnswer = answers[currentStepId] ?? null;

  // Persist answers to sessionStorage on change
  useEffect(() => {
    saveOnboardingAnswers(answers);
  }, [answers]);

  // Store answer only — no navigation
  const setAnswer = useCallback(
    (value: any) => {
      const newAnswers = { ...answers, [currentStepId]: value };

      // If changing an answer that affects branching, prune later answers
      const currentIndex = stepHistory.indexOf(currentStepId);
      if (currentIndex < stepHistory.length - 1) {
        const stepsToRemove = stepHistory.slice(currentIndex + 1);
        stepsToRemove.forEach((s) => delete newAnswers[s]);
        setStepHistory((prev) => prev.slice(0, currentIndex + 1));
      }

      setAnswers(newAnswers);
    },
    [answers, currentStepId, stepHistory]
  );

  // Advance to next step using current answers
  const advance = useCallback(() => {
    const next = getNextStep(currentStepId, answers);
    if (next) {
      const currentIndex = stepHistory.indexOf(currentStepId);
      setStepHistory((prev) => {
        const trimmed = prev.slice(0, currentIndex + 1);
        return [...trimmed, next];
      });
    }
  }, [answers, currentStepId, stepHistory]);

  // Store answer AND advance (for auto-advance single-select steps)
  const answerAndAdvance = useCallback(
    (value: any) => {
      const newAnswers = { ...answers, [currentStepId]: value };

      const currentIndex = stepHistory.indexOf(currentStepId);
      if (currentIndex < stepHistory.length - 1) {
        const stepsToRemove = stepHistory.slice(currentIndex + 1);
        stepsToRemove.forEach((s) => delete newAnswers[s]);
      }

      setAnswers(newAnswers);

      const next = getNextStep(currentStepId, newAnswers);
      if (next) {
        setStepHistory((prev) => {
          const trimmed = prev.slice(0, currentIndex + 1);
          return [...trimmed, next];
        });
      }
    },
    [answers, currentStepId, stepHistory]
  );

  const goBack = useCallback(() => {
    if (stepHistory.length > 1) {
      setStepHistory((prev) => prev.slice(0, -1));
    }
  }, [stepHistory]);

  const getOptions = useCallback((): StepOption[] => {
    if (currentStepId === 'training_purpose') {
      return getTrainingPurposeOptions(answers.experience_level ?? null);
    }

    // Filter long_run_day to exclude rest days
    if (currentStepId === 'long_run_day_preference') {
      const restDays: number[] = answers.training_availability || [];
      const baseOptions = stepDefinition.options || [];
      return baseOptions.filter((o) => !restDays.includes(o.value));
    }

    return stepDefinition.options || [];
  }, [currentStepId, answers, stepDefinition]);

  const isAnswered = currentAnswer !== null && currentAnswer !== undefined && currentAnswer !== '';
  const canGoBack = stepHistory.length > 1;
  const canSkip = stepDefinition.canSkip;
  const isLastStep = currentStepId === 'summary';

  const progress = stepDefinition.progress;

  const getOnboardingSessionData = useCallback((): OnboardingSessionData => {
    // Parse name
    const fullName: string = answers.initial_onboarding || '';
    const nameParts = fullName.trim().split(/\s+/);
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || undefined;

    // Build training distance
    let trainingDistance: string | TrainingDistance | undefined;
    const programme = answers.programme_type;
    if (programme) {
      const dist = answers.training_distance;
      trainingDistance = {
        name: programme,
        distance: dist?.distance,
        unit: dist?.unit,
      };
    }

    // Build community participation
    let communityParticipation: CommunityParticipation | undefined;
    const community = answers.running_community_participation;
    if (community && community !== RunningCommunity.NONE) {
      communityParticipation = {
        type: community,
        distance:
          community === RunningCommunity.RUN_CLUB
            ? answers.run_club_distance
            : ({ distance: 5, unit: DistanceUnit.KM } as UserDistance),
        day: answers.run_club_day,
        difficulty: answers.run_club_intensity,
      };
    }

    // Build training intensity
    const intensityLevel = answers.training_intensity_preference;
    const trainingIntensity: TrainingIntensity = {
      level: typeof intensityLevel === 'number' ? intensityLevel : undefined,
      days: answers.intensive_workout_days_preference,
    };

    // Goal time / PB — extract seconds or undefined
    const parseTimeAnswer = (val: any): number | undefined => {
      if (typeof val === 'number') return val;
      if (typeof val === 'string') return undefined; // skip option was selected
      return undefined;
    };

    // Longest run distance
    const recentHistory = answers.recent_running_history;
    const longestRunDistance: UserDistance | undefined =
      recentHistory && typeof recentHistory === 'object'
        ? recentHistory
        : undefined;

    // Start date
    const startDate: string =
      answers.training_start_date ||
      new Date().toISOString().split('T')[0];

    // Build race event
    let raceEvent: RaceEvent | undefined;
    const raceSelection = answers.race_selection;
    if (raceSelection && typeof raceSelection === 'object') {
      raceEvent = {
        id: raceSelection.id,
        title: raceSelection.title,
        location: raceSelection.location,
        startDate: raceSelection.startDate || answers.race_date,
      };
    }

    const onboardingData: OnboardingData = {
      training_purpose: answers.training_purpose,
      training_distance: trainingDistance,
      experience_level: answers.experience_level as ExperienceLevel,
      longest_run_distance: longestRunDistance,
      longest_run_time: parseTimeAnswer(answers.recent_longest_run_time),
      goal_time: parseTimeAnswer(answers.goal_time),
      current_pb: parseTimeAnswer(answers.current_pb),
      longest_run_day: answers.long_run_day_preference,
      training_intensity: trainingIntensity,
      community_participation: communityParticipation,
      number_of_workouts: answers.weekly_training_frequency,
      number_of_weeks: answers.training_duration,
      days_cant_train: answers.training_availability,
      distance_unit: answers.distance_unit_preference as DistanceUnit,
      additional_info: answers.additional_information,
    };

    return {
      first_name: firstName,
      last_name: lastName,
      gender: answers.gender || '',
      birth_date: answers.date_of_birth || '',
      onboarding_data: onboardingData,
      start_date: startDate,
      race_event: raceEvent,
      is_new_plan: true,
    };
  }, [answers]);

  return {
    currentStepId,
    stepDefinition,
    answers,
    stepHistory,
    progress,
    currentAnswer,
    setAnswer,
    answerAndAdvance,
    advance,
    goBack,
    canGoBack,
    canSkip,
    isLastStep,
    isAnswered,
    getOptions,
    getOnboardingSessionData,
  };
}
